// @flow
import * as React from 'react'

import { Images } from '@/constants'
import { classnames, useSwipe } from '@/helpers'

import styles from './styles.scss'

const Slide = ({
  image,
  isActive,
  fullScreen,
  goBack,
  goNext,
  toggleFullScreen,
}: {
  image: string,
  isActive: boolean,
  fullScreen: boolean,
  goBack: () => void,
  goNext: () => void,
  toggleFullScreen: () => void,
}) => {
  const backgroundRef = React.useRef<HTMLDivElement | null>(null)
  const {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useSwipe({ onSwipeLeft: goNext, onSwipeRight: goBack })

  React.useEffect(() => {
    if (backgroundRef.current && image) {
      backgroundRef.current.style.backgroundImage = `url(${image})`;
    }
  }, [image])

  return (
    <div className={classnames(
      styles.slide,
      {
        [styles.isActive]: isActive,
        [styles.fullScreen]: fullScreen,
      }
    )}>
      <div ref={backgroundRef} className={styles.background}/>
      <div className={classnames(styles.content, {[styles.fullScreen]: fullScreen})}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img className={styles.currentImage} src={image} alt="Hotel" />
        <div className={styles.arrowBack}
          role="button"
          onClick={goBack}
          onKeyDown={goBack}
          tabIndex={-1}
        >
          <img src={Images.arrowLeftWhite}
            alt="back"
          />
        </div>
        <div className={styles.arrowNext}
          role="button"
          onClick={goNext}
          onKeyDown={goNext}
          tabIndex={-1}
        >
          <img src={Images.arrowRightWhite}
            alt="back"
          />
        </div>
        <div className={styles.fullScreenToggle}
          role="button"
          onClick={toggleFullScreen}
          onKeyDown={toggleFullScreen}
          tabIndex={-1}
        >
          <img src={fullScreen ? Images.minimize : Images.maximize}
            alt={fullScreen ? 'Go to fullscreen' : 'Leave fullscreen'}
          />
        </div>
      </div>
    </div>
  )
}

type CarouselProps = {
  images: Array<string>,
}

const Carousel = ({ images }: CarouselProps) => {
  const [fullScreen, setFullscreen] = React.useState<boolean>(false)
  const [currentIndex, setcurrentIndex] = React.useState<number | typeof undefined>(undefined)

  const prevIndex = React.useMemo(() => {
    if (currentIndex === undefined) return undefined
    return currentIndex > 0
      ? currentIndex - 1
      : images.length - 1
  }, [currentIndex])

  const nextIndex = React.useMemo(() => {
    if (currentIndex === undefined) return undefined
    return Math.floor((currentIndex + 1) / images.length) === 0
      ? currentIndex + 1
      : (currentIndex + 1) % images.length
  }, [currentIndex])

  React.useEffect(() => {
    if (images && images.length > 0) setcurrentIndex(0)
    else setcurrentIndex(undefined)
  }, [images])

  const goToNext = () => {
    if (!images || images.length === 0 || currentIndex === undefined) return
    setcurrentIndex(nextIndex)
  }

  const goToBack = () => {
    if (!images || images.length === 0 || currentIndex === undefined) return
    setcurrentIndex(prevIndex)
  }

  const toggleFullScreen = () => { setFullscreen(!fullScreen) }

  return images && images.length > 0 && currentIndex !== undefined 
    ? (
      <div className={styles.carousel}>
        {
          images
            .map((image, i) => ({ idx: i, image }))
            .filter((_, i) => [prevIndex, currentIndex, nextIndex].includes(i))
            .map((image) => (
              <Slide
                key={image.image}
                image={image.image}
                isActive={image.idx === currentIndex}
                fullScreen={fullScreen}
                goBack={goToBack}
                goNext={goToNext}
                toggleFullScreen={toggleFullScreen}
              />
            ))
        }
      </div>
    )
    : <div />
}

export default Carousel;
